import { Component, Input, OnInit } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IBaseGetByComanyRequest, IBaseSearchRequest, IFilter, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import * as InstallationActions from "../../stores/global/installations/installation-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { IInstallation } from '../models/installation';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EmployeeService } from 'src/app/company/services/employee.service';
import { IEmployee } from 'src/app/company/models/employee';
import { InstallationService } from '../services/installation.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

@UntilDestroy()
@Component({
    selector: 'app-installation-view-client',
    templateUrl: './installation-view-client.component.html',
    styleUrl: './installation-view-client.component.scss',
    standalone: false
})
export class InstallationViewClientComponent implements OnInit {

  ////////////////
  // variables //
  //////////////

  @Input() reloadResults : boolean = true;
  vm$: Observable<any>;
  keyword : string;
  showReset = false;
  selectedCategoryId : string;
  companyId: string;
  currentEmployee: Observable<IEmployee>;
  installations: IGridResult<IInstallation[]>;
  loading: boolean = false;

  // Grid Form
  public filterForm = new UntypedFormGroup({
    name: new UntypedFormControl('')
  });

  constructor(
    private store: Store<AppStateInterface>,
    private employeeService: EmployeeService,
    private installationService: InstallationService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.vm$ = combineLatest([
      this.gridState$.pipe(),
      this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {
        if (employee?.company?.id) {
          this.companyId = employee.company.id;
      
          // Update the search request with the new companyId
          this.searchRequest$.next({
            ...this.searchRequest$.value,
            companyId: this.companyId,
          });
        }
      })),
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;
          // Patch the companyId onto the request
          const updatedRequest = { ...request, companyId: this.companyId };

          // Call the installation service with the updated request
          return this.installationService.searchInstallations(updatedRequest);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(res => {
          var gridData : GridDataResult = {
            data: res.data,
            total: res.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      ),
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({params, searchRequest})),
        tap(data => {
          if(JSON.stringify(data.params) !== "{}")
          {
            this.patchFilterForm(data.params);
            this.applyFilter(data.searchRequest);
          }
        }),
        take(1)
      )
    ]).pipe(
      map(([
        gridState,
        currentEmployee,
        installations,
        searchRequest,
        pageChangeSub,
        filterForm
      ]) => ({
        gridState,
        currentEmployee,
        installations,
        searchRequest,
        pageChangeSub,
        filterForm
      }))
    )
  }

  ngOnInit(): void {
  }

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetByComanyRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();


  ////////////////
  // functions //
  //////////////

  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      name: params['name'] || null
    });
  }

  applyFilter(request: IBaseGetByComanyRequest): void {
    var formValues = this.filterForm.value;

      const searchRequest: IBaseGetByComanyRequest = {
        ...request,
      };
  
      // Trim the name before using
      const name = formValues.name ? formValues.name.trim() : null;
  
      // Fields to search for the name
      const searchFields = ['name'];
    
      // Update URL with the filter parameters
      const queryParams = {
        name: formValues.name || null,
      };
  
      if (checkProperties(queryParams)) {
        this.showReset = false;
      } else {
        this.showReset = true;
      }
  
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
  
      if (!name && !formValues.companyId) {
        this.onReset();
        return;
      }
  
      // Create keyword filters
      const nameFilters: IFilter[] = name 
          ? searchFields.map(field => ({ field, operator: 'contains', value: name })) 
          : [];
  
      // Other filters
      var filterObject : IFilter = {
        logic: "and",
        filters: []
      }
  
      // Other filters
      var otherFilters: IFilter[] =[
          { field: 'companyId', operator: 'eq', value: formValues.companyId }
      ];
  
      // remove empty filters
      otherFilters = otherFilters.filter(x => x.value != null)
  
      // Combine name filters with 'or' logic if there are any
      const combinedNameFilter: IFilter = { logic: 'or', filters: nameFilters }
  
      // Combine name filters with other filters using 'and' logic
      const allFilters: IFilter[] = combinedNameFilter.filters.length > 0
          ? [combinedNameFilter, ...otherFilters]
          : otherFilters;
  
      // Apply filters to devices
      if (allFilters.length === 0) {
          return;
      }
  
      filterObject.filters = allFilters;
  
      searchRequest.advancedFilter = filterObject;
  
      this.searchRequest$.next(searchRequest);
  }

  
  resetFilter(): void {
    // reset form
    this.filterForm.reset();
    this.showReset = false;

    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small, companyId: this.companyId})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }

  onReset(){
    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small, companyId: this.companyId})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    this.keyword = null;
  } 
}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}